// @ts-nocheck
/**
 * AuthContext
 *
 * Single source of truth for "where in the auth lifecycle am I?", derived
 * entirely from the server via `GET /v1/me` (cookie session — the frontend
 * holds no token). The boot probe and `refreshUser()` are the only ways the
 * phase changes, so login/signup/verify screens never set auth state by hand;
 * they act, then call `refreshUser()` and let the gate decide where to land.
 *
 * Phases:
 *   loading → probe in flight
 *   app     → session valid AND email verified AND org active  (full access)
 *   verify  → session valid BUT email unverified / org inactive (must verify)
 *   auth    → no session                                        (must sign in)
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth/auth.service';
import { MeUser, MeOrg } from '../schema/auth/me.schema';
import {
  redirectToNotInPlan,
  redirectToPlatformAuth,
} from '../navigation/platformAuthRedirect';
import { isNotInPlan } from '../lib/planGate';

export type AuthPhase = 'loading' | 'app' | 'verify' | 'auth';

interface AuthContextType {
  phase: AuthPhase;
  user: MeUser | null;
  org: MeOrg | null;
  /**
   * The current user's membership role in the active org, from `/me`.
   * Canonical platform roles: 'org_admin' (workspace admin/owner) | 'admin'
   * (regular agent) | 'external'. Null when there is no active membership.
   */
  role: string | null;
  /** Convenience flags derived from `phase` / `role`. */
  isLoading: boolean;
  isAuthenticated: boolean;
  needsVerification: boolean;
  /**
   * Workspace administrator (org_admin). Admins see the whole workspace;
   * regular agents are scoped to the tickets assigned to them.
   */
  isAdmin: boolean;
  /** Re-probe the session and recompute the phase. */
  refreshUser: () => Promise<void>;
  /** Tear down the session (server + local) and drop to the auth phase. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Full access requires BOTH a verified email and an active org (requirement:
// "only allow the user after email verification and the org is verified").
function isFullyVerified(user: MeUser | null, org: MeOrg | null): boolean {
  return !!user?.email_verified && org?.status === 'active';
}

// Resolve the user's role in the active org from the `/me` memberships list.
// Falls back to the first active membership when the org match is absent (the
// platform only returns memberships the user actually holds).
function resolveRole(memberships: Array<{ orgId: string; role: string }> | undefined, orgId: string | undefined): string | null {
  if (!memberships?.length) return null;
  const match = orgId ? memberships.find((m) => m.orgId === orgId) : undefined;
  return (match ?? memberships[0])?.role ?? null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [phase, setPhase] = useState<AuthPhase>('loading');
  const [user, setUser] = useState<MeUser | null>(null);
  const [org, setOrg] = useState<MeOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);

  const evaluate = useCallback(async () => {
    try {
      const res = await authService.me();

      // Signed in, but this org's plan does not include the Tickets website
      // (SK Salon / SK Fleet use Tickets underneath without selling it). No
      // list = open. The session stays; the browser leaves, so no phase change.
      if (isNotInPlan(res.data?.subscribed_products)) {
        redirectToNotInPlan();
        return;
      }

      const nextUser = res.data?.user ?? null;
      const nextOrg = res.data?.org ?? null;
      setUser(nextUser);
      setOrg(nextOrg);
      setRole(resolveRole(res.data?.memberships, nextOrg?.id));
      const fullyVerified = isFullyVerified(nextUser, nextOrg);
      setPhase(fullyVerified ? 'app' : 'verify');
      if (!fullyVerified) {
        // Session exists but the email is unverified / org inactive. There is no
        // local verification screen anymore — the SK Platform owns that flow, so
        // bounce there (it shows the right screen for this half-authed state).
        redirectToPlatformAuth();
      }
    } catch {
      // No session → bounce to the centralized platform auth (product=tickets).
      // Platform owns login/signup; it returns here after sign-in. We set 'auth'
      // as a fallback state, but the redirect navigates away on web.
      setUser(null);
      setOrg(null);
      setRole(null);
      setPhase('auth');
      redirectToPlatformAuth();
    }
  }, []);

  const refreshUser = useCallback(async () => {
    authService.invalidateMeCache();
    await evaluate();
  }, [evaluate]);

  // Boot probe — runs once on mount.
  useEffect(() => {
    evaluate();
  }, [evaluate]);

  // The api-client dispatches these when a 401 refresh fails (session truly
  // gone). Drop straight to the auth phase.
  useEffect(() => {
    const handler = () => {
      setUser(null);
      setOrg(null);
      setRole(null);
      setPhase('auth');
      redirectToPlatformAuth();
    };
    window.addEventListener('auth:expired', handler);
    window.addEventListener('auth:logout', handler);
    return () => {
      window.removeEventListener('auth:expired', handler);
      window.removeEventListener('auth:logout', handler);
    };
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setOrg(null);
    setRole(null);
    setPhase('auth');
    // Signed out → back to the centralized platform auth (no local logged-out UI).
    redirectToPlatformAuth();
  }, []);

  const value: AuthContextType = {
    phase,
    user,
    org,
    role,
    isLoading: phase === 'loading',
    isAuthenticated: phase === 'app',
    needsVerification: phase === 'verify',
    isAdmin: role === 'org_admin',
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
